import React, { useEffect, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';
import {
    EVENT_TYPES, REASON_CODES, addDays, dayLabel, errorText, firstOfMonth, hm, isoDate, lastOfMonth,
    nowLocalInput, reasonLabel, sourceLabel, surface, time, tone, typeLabel,
} from './tcFormat';

/** One call to the auth timeclock route. */
export const tcPost = (action, api, body = {}) =>
    SftGlobalData.post('timeclock&action=' + action, { key: api, ...body });

// ---------------------------------------------------------------------------
//  Correction: add a missing stamp, change one (REPLACE) or void one
// ---------------------------------------------------------------------------

/**
 * @param preset {action: ADD|REPLACE|VOID, user_id, user_name, work_date, event_type, event (for REPLACE/VOID), store_id}
 */
export const CorrectionDialog = ({ open, preset, onClose, onDone, t, lang, api, employees }) => {
    const action = preset?.action || 'ADD';
    const [userId, setUserId] = useState('');
    const [type, setType] = useState('OUT');
    const [at, setAt] = useState('');
    const [reasonCode, setReasonCode] = useState('FORGOT_OUT');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !preset) return;
        setError('');
        setReason('');
        setUserId(preset.user_id || '');
        if (action === 'ADD') {
            const proposed = preset.event_type || 'OUT';
            setType(proposed);
            setReasonCode(proposed === 'IN' ? 'FORGOT_IN' : proposed === 'OUT' ? 'FORGOT_OUT' : 'FORGOT_BREAK');
            const date = preset.work_date || isoDate(new Date());
            const hour = proposed === 'OUT' ? '18:00' : proposed === 'IN' ? '09:00' : '12:30';
            const proposal = date + 'T' + hour;
            setAt(proposal > nowLocalInput() ? nowLocalInput() : proposal);
        } else {
            const event = preset.event || {};
            setType(event.event_type || 'IN');
            setAt(event.event_at ? event.event_at.substring(0, 16).replace(' ', 'T') : nowLocalInput());
            setReasonCode(action === 'VOID' ? 'DUPLICATE' : 'WRONG_TIME');
        }
    }, [open, preset, action]);

    const title = action === 'ADD' ? t('tc_add_stamp', 'Add missing stamp')
        : action === 'REPLACE' ? t('tc_change_stamp', 'Change stamp')
            : t('tc_void_stamp', 'Void stamp');

    const submit = () => {
        if (reasonCode === 'OTHER' && reason.trim() === '') {
            setError(t('tc_err_reason', 'Give an explanation.'));
            return;
        }
        if (action === 'ADD' && userId === '') {
            setError(t('tc_err_employee', 'Choose an employee.'));
            return;
        }
        const body = { action, reason_code: reasonCode, reason: reason.trim() };
        if (action === 'ADD') {
            Object.assign(body, { user_id: userId, event_type: type, event_at: at, store_id: preset.store_id || '' });
        } else {
            body.event_id = preset.event.id;
            if (action === 'REPLACE') Object.assign(body, { event_type: type, event_at: at });
        }
        setBusy(true);
        setError('');
        tcPost('correct', api, body).then((response) => {
            setBusy(false);
            if (response?.success) {
                onDone(response.data);
            } else {
                setError(errorText(t, response));
            }
        });
    };

    const original = preset?.event;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                    {(preset?.user_name || '') + (preset?.work_date ? ' · ' + dayLabel(preset.work_date, lang) : '')}
                    {original && action !== 'ADD' && (' · ' + typeLabel(t, original.event_type) + ' ' + time(original.event_at))}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {action === 'ADD' && !preset?.user_id && (
                        <TextField select size="small" label={t('tc_employee', 'Employee')} value={userId}
                            onChange={(e) => setUserId(e.target.value)}>
                            {(employees || []).filter((e) => e.is_active).map((e) => (
                                <MenuItem key={e.id} value={e.id}>{e.name} ({e.id})</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {action !== 'VOID' && (
                        <React.Fragment>
                            <TextField select size="small" label={t('tc_field_type', 'Action')} value={type}
                                onChange={(e) => setType(e.target.value)}>
                                {EVENT_TYPES.map((code) => <MenuItem key={code} value={code}>{typeLabel(t, code)}</MenuItem>)}
                            </TextField>
                            <TextField size="small" type="datetime-local" label={t('tc_field_when', 'Date and time')}
                                value={at} onChange={(e) => setAt(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: nowLocalInput() } }} />
                        </React.Fragment>
                    )}
                    <TextField select size="small" label={t('tc_field_reason', 'Reason')} value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}>
                        {REASON_CODES.map((code) => <MenuItem key={code} value={code}>{reasonLabel(t, code)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" multiline minRows={2} label={t('tc_field_reason_text', 'Explanation')}
                        value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 255 }} />
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {t('tc_correction_note_web', 'The original stamps stay in the log. The correction is recorded on your user and appears in the inspection export.')}
                    </Typography>
                    {error && <Typography sx={{ fontSize: 13.5, color: tone.bad }}>{error}</Typography>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={submit} disabled={busy} disableElevation
                    color={action === 'VOID' ? 'error' : 'primary'} sx={{ textTransform: 'none' }}>
                    {busy ? t('tc_busy', 'One moment…') : title}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
//  Decision on a request
// ---------------------------------------------------------------------------

export const DecisionDialog = ({ open, request, approve, onClose, onDone, t, lang, api }) => {
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { if (open) { setRemark(''); setError(''); } }, [open]);

    const submit = () => {
        setBusy(true);
        tcPost('decide', api, { id: request.id, decision: approve ? 'APPROVE' : 'REJECT', remark }).then((response) => {
            setBusy(false);
            if (response?.success) onDone();
            else setError(errorText(t, response));
        });
    };

    if (!request) return null;
    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>{approve ? t('tc_approve', 'Approve') : t('tc_reject', 'Reject')}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 14, mb: 2 }}>{describeRequest(t, lang, request)}</Typography>
                <TextField fullWidth size="small" label={t('tc_remark_hint', 'Remark (optional)')} value={remark}
                    onChange={(e) => setRemark(e.target.value)} inputProps={{ maxLength: 255 }} />
                {error && <Typography sx={{ fontSize: 13.5, color: tone.bad, mt: 1.5 }}>{error}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={submit} disabled={busy}
                    color={approve ? 'primary' : 'error'} sx={{ textTransform: 'none' }}>
                    {approve ? t('tc_approve', 'Approve') : t('tc_reject', 'Reject')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** "Els · ma 14/09 · asks OUT 18:31 instead of 18:02 · "customer after closing"" */
export const describeRequest = (t, lang, event) => {
    const parts = [(event.user_name || event.user_id) + ' · ' + dayLabel(event.event_at, lang)];
    if (!event.correction) {
        parts.push(t('tc_late_stamp', 'late offline stamp') + ' ' + typeLabel(t, event.event_type) + ' ' + time(event.event_at));
    } else if (event.correction === 'VOID') {
        parts.push(t('tc_asks_void', 'asks to void') + ' ' + (event.corrects ? typeLabel(t, event.corrects.event_type) + ' ' + time(event.corrects.event_at) : ''));
    } else {
        let text = t('tc_asks', 'asks') + ' ' + typeLabel(t, event.event_type) + ' ' + time(event.event_at);
        if (event.corrects) text += ' ' + t('tc_instead_of', 'instead of') + ' ' + time(event.corrects.event_at);
        parts.push(text);
    }
    parts.push(reasonLabel(t, event.reason_code) + (event.reason ? ': "' + event.reason + '"' : ''));
    return parts.join(' · ');
};

/** Outcome of the "near the store" check of an own-phone stamp. */
const locationLabel = (t, event) => {
    const flags = event.flags || [];
    if (flags.includes('LOCATION_MOCKED')) return t('tc_loc_mocked', 'fake location');
    if (flags.includes('LOCATION_DOUBT')) return t('tc_loc_doubt', 'location uncertain');
    if (flags.includes('LOCATION_UNCHECKED')) return t('tc_loc_unchecked', 'location not checked (store without position)');
    return {
        INSIDE: t('tc_loc_inside', 'at the store'),
        OUTSIDE: t('tc_loc_outside', 'outside the store radius'),
        UNKNOWN: t('tc_loc_unknown', 'no location'),
    }[event.location_status] || '';
};

// ---------------------------------------------------------------------------
//  One day of one employee: the stamps behind it
// ---------------------------------------------------------------------------

export const DayDialog = ({ open, day, onClose, onCorrect, t, lang, api, theme }) => {
    const [events, setEvents] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !day) return;
        setEvents(null);
        setError('');
        tcPost('dayEvents', api, { user_id: day.user_id, work_date: day.work_date }).then((response) => {
            if (response?.success) setEvents(response.data?.events || []);
            else setError(errorText(t, response));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, day, api]);

    if (!day) return null;
    const line = surface(theme).line;

    // Stamps of this work day: from the first minute of the day, plus the
    // morning after when the shift ran past midnight (last_out on the next day).
    const until = day.last_out && day.last_out.substring(0, 10) > day.work_date ? day.last_out : day.work_date + ' 23:59:59';
    const shown = (events || []).filter((e) => e.event_at >= day.work_date && e.event_at <= until);

    const statusChip = (event) => {
        if (event.status === 'REJECTED') return <Chip size="small" label={t('tc_refused', 'Refused')} sx={{ bgcolor: tone.bad + '22', color: tone.bad }} />;
        if (event.status === 'PENDING' && !event.decision) return <Chip size="small" label={t('tc_pending_label', 'Waiting for approval')} sx={{ bgcolor: tone.warn + '22', color: tone.warn }} />;
        if (event.decision?.decision === 'REJECTED') return <Chip size="small" label={t('tc_rejected_done', 'Rejected')} sx={{ bgcolor: tone.bad + '22', color: tone.bad }} />;
        if (event.superseded_by_id) return <Chip size="small" label={t('tc_superseded', 'Corrected later')} variant="outlined" />;
        if (event.correction === 'VOID') return <Chip size="small" label={t('tc_voids', 'Voids a stamp')} variant="outlined" />;
        return null;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>
                {day.user_name} · {dayLabel(day.work_date, lang)}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.5 }}>
                    {t('tc_worked', 'Worked')} {hm(day.worked_minutes, lang)} · {t('tc_break', 'Break')} {hm(day.break_minutes, lang)}
                    {day.deducted_break_minutes > 0 && (' · ' + t('tc_deducted', 'deducted') + ' ' + hm(day.deducted_break_minutes, lang))}
                </Typography>
                {error && <Typography sx={{ color: tone.bad, fontSize: 14 }}>{error}</Typography>}
                {!events && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <TailSpin height="28" width="28" color={Colors.sft_blue} ariaLabel="loading" />
                    </Box>
                )}
                {events && shown.length === 0 && (
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('tc_no_stamps', 'No stamps on this day.')}</Typography>
                )}
                {shown.map((event) => {
                    const faded = !event.is_effective;
                    return (
                        <Box key={event.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, borderBottom: line }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, width: 48, fontVariantNumeric: 'tabular-nums', opacity: faded ? 0.5 : 1, textDecoration: event.superseded_by_id ? 'line-through' : 'none' }}>
                                {time(event.event_at)}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, opacity: faded ? 0.6 : 1 }}>{typeLabel(t, event.event_type)}</Typography>
                                    {statusChip(event)}
                                </Box>
                                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                                    {sourceLabel(t, event.source)}
                                    {event.location_status ? ' · ' + locationLabel(t, event) : ''}
                                    {event.store_id ? ' · ' + t('tc_store', 'Store') + ' ' + event.store_id : ''}
                                    {event.correction ? ' · ' + t('tc_by', 'by') + ' ' + (event.created_by_name || event.created_by || '') + ' · ' + reasonLabel(t, event.reason_code) + (event.reason ? ': "' + event.reason + '"' : '') : ''}
                                    {event.reject_code ? ' · ' + event.reject_code : ''}
                                    {event.decision?.remark ? ' · "' + event.decision.remark + '"' : ''}
                                </Typography>
                            </Box>
                            {event.is_effective && event.status === 'ACCEPTED' && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Button size="small" sx={{ textTransform: 'none', minWidth: 0 }}
                                        onClick={() => onCorrect({ action: 'REPLACE', event, user_id: day.user_id, user_name: day.user_name, work_date: day.work_date })}>
                                        {t('tc_change', 'Change')}
                                    </Button>
                                    <Button size="small" color="error" sx={{ textTransform: 'none', minWidth: 0 }}
                                        onClick={() => onCorrect({ action: 'VOID', event, user_id: day.user_id, user_name: day.user_name, work_date: day.work_date })}>
                                        {t('tc_void', 'Void')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </DialogContent>
            <DialogActions>
                <Button sx={{ textTransform: 'none' }}
                    onClick={() => onCorrect({
                        action: 'ADD', user_id: day.user_id, user_name: day.user_name, work_date: day.work_date, store_id: day.store_id,
                        event_type: (day.anomaly_flags || []).includes('MISSING_IN') ? 'IN' : 'OUT',
                    })}>
                    ＋ {t('tc_add_stamp', 'Add missing stamp')}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('close', 'Close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
//  Exports
// ---------------------------------------------------------------------------

const downloadText = (filename, content, mime) => {
    const blob = new Blob([content], { type: mime || 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const ExportDialog = ({ open, initialLayout, storeId, onClose, t, api, notify }) => {
    const today = isoDate(new Date());
    const lastMonth = addDays(firstOfMonth(today), -1);
    const [layout, setLayout] = useState('payroll');
    const [from, setFrom] = useState(firstOfMonth(lastMonth));
    const [to, setTo] = useState(lastMonth);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { if (open) { setLayout(initialLayout || 'payroll'); setError(''); } }, [open, initialLayout]);

    const preset = (which) => {
        if (which === 'last') { setFrom(firstOfMonth(lastMonth)); setTo(lastMonth); }
        if (which === 'this') { setFrom(firstOfMonth(today)); setTo(lastOfMonth(today) > today ? today : lastOfMonth(today)); }
    };

    const submit = () => {
        if (!from || !to || from > to) { setError(t('tc_err_period', 'Choose a valid period.')); return; }
        setBusy(true);
        setError('');
        tcPost('export', api, { from_date: from, to_date: to, layout, format: 'csv', store_id: storeId || '' }).then((response) => {
            setBusy(false);
            const file = response?.data;
            if (response?.success && file && typeof file.content === 'string' && !file.rows) {
                // Nothing to export: say so instead of downloading an empty file.
                setError(t('tc_export_empty', 'No time registrations in this period. Choose another period or store.'));
            } else if (response?.success && file && typeof file.content === 'string') {
                downloadText(file.filename || ('prikklok_' + layout + '_' + from + '_' + to + '.csv'), file.content, file.mime_type);
                if (notify) notify(t('tc_export_done', 'Export downloaded') + ' (' + (file.rows || 0) + ')', true);
                onClose();
            } else {
                setError(errorText(t, response));
            }
        });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>{t('tc_export', 'Export')}</DialogTitle>
            <DialogContent>
                <ToggleButtonGroup exclusive size="small" value={layout} onChange={(e, v) => v && setLayout(v)} sx={{ mb: 1.5, mt: 1 }}>
                    <ToggleButton value="payroll" sx={{ textTransform: 'none' }}>{t('tc_export_payroll', 'Payroll office')}</ToggleButton>
                    <ToggleButton value="inspection" sx={{ textTransform: 'none' }}>{t('tc_export_inspection', 'Inspection')}</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                    {layout === 'payroll'
                        ? t('tc_export_payroll_hint', 'One line per employee per day: hours in hundredths, ISO week, store, first in, last out, corrected. CSV with ; for Excel and the payroll office.')
                        : t('tc_export_inspection_hint', 'Per employee per day every stamp with its source, the corrections with author and reason, and the result of the hash chain check.')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Chip size="small" label={t('tc_last_month', 'Last month')} onClick={() => preset('last')} />
                    <Chip size="small" label={t('tc_this_month', 'This month')} onClick={() => preset('this')} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField size="small" type="date" label={t('tc_from', 'From')} value={from}
                        onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
                    <TextField size="small" type="date" label={t('tc_to', 'To')} value={to}
                        onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
                </Box>
                {error && <Typography sx={{ fontSize: 13.5, color: tone.bad, mt: 1.5 }}>{error}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={submit} disabled={busy} sx={{ textTransform: 'none' }}>
                    {busy ? t('tc_busy', 'One moment…') : t('tc_download_csv', 'Download CSV')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
